import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiSave,
  FiX,
  FiImage,
  FiUpload,
  FiDollarSign,
  FiTruck,
  FiMapPin,
  FiNavigation,
  FiPlus,
} from "react-icons/fi";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { uploadAPI } from "../../services/api";
import toast from "react-hot-toast";
import IngredientSearch from "../../components/IngredientSearch";

const MealFormContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const MealFormContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageDescription = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const FormCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const FormHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0;
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select,
  textarea {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }

    &.error {
      border-color: #dc3545;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
`;

const ImageUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ImagePreview = styled.div`
  width: 100%;
  max-width: 400px;
  height: 200px;
  border: 2px dashed ${(props) => (props.$isDragging ? "#2d5016" : "#e0e0e0")};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${(props) => (props.$isDragging ? "#f0f8f0" : "#f8f9fa")};
  cursor: pointer;
  transition: all 0.3s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: ${(props) => (props.$isDragging ? "#2d5016" : "#999")};
  }
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #2d5016;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.3s ease;

  &:hover {
    background: #1a3a0e;
  }

  input {
    display: none;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.$primary ? "#2d5016" : "white")};
  color: ${(props) => (props.$primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "#1a3a0e" : "#f8f9fa")};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const schema = yup.object().shape({
  mealName: yup.string().required("Meal name is required"),
  basePrice: yup
    .number()
    .positive("Price must be positive")
    .required("Base price is required"),
  deliveryFee: yup.number().min(0, "Delivery fee cannot be negative"),
});

const MealForm = ({ meal = null, onSave, onCancel }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(meal?.image_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [offersDelivery, setOffersDelivery] = useState(
    meal?.offers_delivery ?? meal?.offersDelivery ?? true
  );
  const [offersPickup, setOffersPickup] = useState(
    meal?.offers_pickup ?? meal?.offersPickup ?? false
  );
  const [pickupAddress, setPickupAddress] = useState(
    meal?.pickup_address || meal?.pickupAddress || ""
  );
  const [pickupInstructions, setPickupInstructions] = useState(
    meal?.pickup_instructions || meal?.pickupInstructions || ""
  );
  const [pickupLatitude, setPickupLatitude] = useState(
    meal?.pickup_latitude ? parseFloat(meal.pickup_latitude) : null
  );
  const [pickupLongitude, setPickupLongitude] = useState(
    meal?.pickup_longitude ? parseFloat(meal.pickup_longitude) : null
  );
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [allergens, setAllergens] = useState(meal?.allergens || []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      mealName: meal?.meal_name || "",
      description: meal?.description || "",
      basePrice: meal?.base_price || "",
      deliveryFee: meal?.delivery_fee || 0,
      calories: meal?.calories || "",
      protein: meal?.protein || "",
      carbs: meal?.carbs || "",
      fat: meal?.fat || "",
    },
  });

  // Load existing ingredients when editing
  useEffect(() => {
    if (meal && meal.ingredients && meal.ingredients.length > 0) {
      setSelectedIngredients(meal.ingredients);
    }
  }, [meal]);

  const processImageFile = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await uploadAPI.uploadImage(formData);
      setUploadedImage(response.data.data.url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    await processImageFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      await processImageFile(file);
    }
  };

  const handleAddAllergen = () => {
    const allergen = prompt("Enter allergen name:");
    if (allergen && allergen.trim()) {
      setAllergens([...allergens, allergen.trim()]);
    }
  };

  const handleRemoveAllergen = (index) => {
    setAllergens(allergens.filter((_, i) => i !== index));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser");
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setPickupLatitude(latitude);
        setPickupLongitude(longitude);
        toast.success("GPS coordinates captured successfully!");
        setIsGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Unable to get your location";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location access denied. Please enable location permissions.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
        }

        toast.error(errorMessage);
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  };

  const onSubmit = async (data) => {
    if (!offersDelivery && !offersPickup) {
      toast.error("Please select at least one fulfillment method");
      return;
    }

    if (offersPickup && !pickupAddress.trim()) {
      toast.error("Pickup address is required when pickup is enabled");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = {
        mealName: data.mealName,
        description: data.description || "",
        basePrice: parseFloat(data.basePrice),
        deliveryFee: parseFloat(data.deliveryFee) || 0,
        offersDelivery,
        offersPickup,
        ingredientIds: selectedIngredients.map((ing) => ing.id),
      };

      // Only include optional fields if they have values
      if (uploadedImage && uploadedImage.trim()) {
        formData.imageUrl = uploadedImage;
      }

      if (allergens && allergens.length > 0) {
        formData.allergens = allergens;
      }

      if (data.calories) {
        formData.calories = parseInt(data.calories);
      }

      if (data.protein) {
        formData.protein = parseFloat(data.protein);
      }

      if (data.carbs) {
        formData.carbs = parseFloat(data.carbs);
      }

      if (data.fat) {
        formData.fat = parseFloat(data.fat);
      }

      if (offersPickup) {
        if (pickupAddress && pickupAddress.trim()) {
          formData.pickupAddress = pickupAddress;
        }
        if (pickupLatitude !== null && pickupLatitude !== undefined) {
          formData.pickupLatitude = parseFloat(pickupLatitude);
        }
        if (pickupLongitude !== null && pickupLongitude !== undefined) {
          formData.pickupLongitude = parseFloat(pickupLongitude);
        }
        if (pickupInstructions && pickupInstructions.trim()) {
          formData.pickupInstructions = pickupInstructions;
        }
      }

      console.log("Form data being sent:", formData);

      await onSave(formData);
    } catch (error) {
      console.error("Error saving meal:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/vendor/meals");
    }
  };

  return (
    <MealFormContainer>
      <MealFormContent>
        <PageDescription>
          <p>{meal ? "Edit" : "Create"} Standalone Meal</p>
        </PageDescription>

        <FormCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <FormHeader>
            <h2>{meal ? "Edit Meal" : "Create New Meal"}</h2>
            <Button onClick={handleCancel} type="button">
              <FiX />
              Cancel
            </Button>
          </FormHeader>

          <FormContent>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <label>Meal Name *</label>
                <input
                  type="text"
                  {...register("mealName")}
                  className={errors.mealName ? "error" : ""}
                  placeholder="e.g., Grilled Chicken Breast"
                />
                {errors.mealName && (
                  <span className="error-message">
                    {errors.mealName.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <label>Description</label>
                <textarea
                  {...register("description")}
                  placeholder="Describe your meal..."
                />
              </FormGroup>

              <ImageUploadSection>
                <label>Meal Image</label>
                <ImagePreview
                  $isDragging={isDragging}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById("image-upload-input")?.click()}
                >
                  {uploadedImage ? (
                    <img src={uploadedImage} alt="Meal preview" />
                  ) : (
                    <div className="placeholder">
                      <FiImage size={48} />
                      <span>{isDragging ? "Drop image here" : "Drag & drop image or click to upload"}</span>
                    </div>
                  )}
                </ImagePreview>
                <UploadButton>
                  <FiUpload />
                  {isUploading ? "Uploading..." : "Upload Image"}
                  <input
                    id="image-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                  />
                </UploadButton>
              </ImageUploadSection>

              <FormRow>
                <FormGroup>
                  <label>Base Price (GYD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register("basePrice")}
                    className={errors.basePrice ? "error" : ""}
                    placeholder="0.00"
                  />
                  {errors.basePrice && (
                    <span className="error-message">
                      {errors.basePrice.message}
                    </span>
                  )}
                </FormGroup>

                <FormGroup>
                  <label>Delivery Fee (GYD)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register("deliveryFee")}
                    placeholder="0.00"
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <label>Ingredients</label>
                <IngredientSearch
                  selectedIngredients={selectedIngredients}
                  onIngredientsChange={setSelectedIngredients}
                />
              </FormGroup>

              <FormGroup>
                <label>Allergens</label>
                <div style={{ marginBottom: "0.5rem" }}>
                  <Button
                    type="button"
                    onClick={handleAddAllergen}
                    style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
                  >
                    <FiPlus />
                    Add Allergen
                  </Button>
                </div>
                {allergens.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    {allergens.map((allergen, index) => (
                      <span
                        key={index}
                        style={{
                          padding: "0.5rem 0.75rem",
                          background: "#fff3cd",
                          border: "1px solid #ffc107",
                          borderRadius: "20px",
                          fontSize: "0.875rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        {allergen}
                        <button
                          type="button"
                          onClick={() => handleRemoveAllergen(index)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#856404",
                          }}
                        >
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <label>Calories</label>
                  <input
                    type="number"
                    min="0"
                    {...register("calories")}
                    placeholder="e.g., 500"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Protein (g)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    {...register("protein")}
                    placeholder="e.g., 30.0"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label>Carbs (g)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    {...register("carbs")}
                    placeholder="e.g., 40.0"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Fat (g)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    {...register("fat")}
                    placeholder="e.g., 20.0"
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <label>Fulfillment Options *</label>
                <CheckboxGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={offersDelivery}
                      onChange={(e) => setOffersDelivery(e.target.checked)}
                    />
                    <FiTruck />
                    Delivery
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={offersPickup}
                      onChange={(e) => setOffersPickup(e.target.checked)}
                    />
                    <FiMapPin />
                    Pickup
                  </CheckboxLabel>
                </CheckboxGroup>
              </FormGroup>

              {offersPickup && (
                <>
                  <FormGroup>
                    <label>Pickup Address *</label>
                    <input
                      type="text"
                      value={pickupAddress}
                      onChange={(e) => setPickupAddress(e.target.value)}
                      placeholder="Enter pickup address"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>📍 GPS Location (Optional)</label>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "flex-start",
                      }}
                    >
                      <button
                        type="button"
                        onClick={getCurrentLocation}
                        disabled={isGettingLocation}
                        style={{
                          padding: "0.75rem 1rem",
                          backgroundColor: isGettingLocation
                            ? "#ccc"
                            : "#2d5016",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: isGettingLocation
                            ? "not-allowed"
                            : "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <FiNavigation />
                        {isGettingLocation
                          ? "Getting Location..."
                          : pickupLatitude && pickupLongitude
                          ? "Update GPS Location"
                          : "Capture GPS Location"}
                      </button>
                      {pickupLatitude && pickupLongitude && (
                        <div
                          style={{
                            flex: 1,
                            padding: "0.75rem",
                            background: "#e8f5e9",
                            border: "1px solid #4caf50",
                            borderRadius: "8px",
                            fontSize: "0.875rem",
                            color: "#2e7d32",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <FiMapPin />
                          {pickupLatitude.toFixed(6)}, {pickupLongitude.toFixed(6)}
                        </div>
                      )}
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <label>Pickup Instructions</label>
                    <textarea
                      value={pickupInstructions}
                      onChange={(e) => setPickupInstructions(e.target.value)}
                      placeholder="e.g., Ring the doorbell, call when you arrive..."
                    />
                  </FormGroup>
                </>
              )}

              <ButtonGroup>
                <Button type="button" onClick={handleCancel}>
                  <FiX />
                  Cancel
                </Button>
                <Button $primary type="submit" disabled={isSubmitting}>
                  <FiSave />
                  {isSubmitting ? "Saving..." : meal ? "Update Meal" : "Create Meal"}
                </Button>
              </ButtonGroup>
            </Form>
          </FormContent>
        </FormCard>
      </MealFormContent>
    </MealFormContainer>
  );
};

export default MealForm;

